import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  DataTable,
  buildListMasterTableColumns,
} from "../../../components/data-table";
import { ListMasterPageLayout } from "../../../components/list-master";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import { toast } from "sonner";
import {
  statesAPI,
  districtsAPI,
  type State,
  type District,
  type DistrictCreatePayload,
} from "../../../services/api";
import { CommonPagination } from "../../../components/CommonPagination";

function getStateName(district: District, states: State[]) {
  if (district.state_name) {
    return district.state_name;
  }
  const id = district.state;
  if (id === null) {
    return "—";
  }
  const s = states.find(p => String(p.id) === String(id));
  return s?.name ?? String(id);
}

export default function DistrictPage() {
  const [states, setStates] = useState<State[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<DistrictCreatePayload>({
    name: "",
    state: "",
    is_active: true,
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editing, setEditing] = useState<District | null>(null);
  const [deleting, setDeleting] = useState<District | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchStates = async () => {
    try {
      const list = await statesAPI.list();
      setStates(Array.isArray(list) ? list : []);
    } catch {
      setStates([]);
    }
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await districtsAPI.listPaginated({
        page: currentPage,
        page_size: pageSize,
        search: searchTerm.trim() || undefined,
      });
      const list = response.data ?? response.results ?? [];
      const count = response.count ?? response.total_count ?? list.length;
      const computedTotalPages =
        response.total_pages ?? Math.ceil(count / pageSize);

      setDistricts(Array.isArray(list) ? list : []);
      setTotalItems(count);
      setTotalPages(computedTotalPages);
    } catch {
      toast.error("Failed to load districts");
      setDistricts([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchList();
    }, 300);

    return () => clearTimeout(timer);
  }, [fetchList]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("District name is required");
      return;
    }
    if (!form.state) {
      toast.error("Please select a state");
      return;
    }
    setSubmitLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        state: Number(form.state) || form.state,
        is_active: form.is_active,
      };
      if (editing) {
        await districtsAPI.update(editing.id, payload);
        toast.success("District updated successfully");
      } else {
        await districtsAPI.create(payload);
        toast.success("District created successfully");
      }
      setDialogOpen(false);
      setForm({ name: "", state: "", is_active: true });
      setEditing(null);
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to save district";
      toast.error(String(msg));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeleteLoading(true);
    try {
      await districtsAPI.delete(deleting.id);
      toast.success("District deleted successfully");
      setDeleting(null);
      if (districts.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to delete district";
      toast.error(String(msg));
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEdit = useCallback(
    (d: District) => {
      setEditing(d);
      setForm({
        name: d.name,
        state:
          d.state !== null
            ? String(d.state)
            : states[0]?.id !== null
              ? String(states[0]?.id ?? "")
              : "",
        is_active: d.is_active ?? true,
      });
      setDialogOpen(true);
    },
    [states]
  );

  const tableColumns = useMemo(
    () =>
      buildListMasterTableColumns<District>({
        currentPage,
        pageSize,
        onEdit: openEdit,
        onDelete: setDeleting,
        showCode: false,
        extraColumns: [
          {
            id: "state",
            label: "State",
            width: 160,
            renderCell: row => getStateName(row, states),
          },
        ],
      }),
    [currentPage, pageSize, openEdit, states]
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground">District</h1>
        <p className="text-muted-foreground">Manage districts</p>
      </div>
      <ListMasterPageLayout
        tableId="list-master-district"
        tableColumns={tableColumns}
        listTitle={`Districts (${totalItems})`}
        searchPlaceholder="Search districts..."
        searchTerm={searchTerm}
        onSearchChange={v => {
          setSearchTerm(v);
          setCurrentPage(1);
        }}
        headerActions={
          <Dialog
            open={dialogOpen}
            onOpenChange={open => {
              setDialogOpen(open);
              if (!open) {
                setForm({ name: "", state: "", is_active: true });
                setEditing(null);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setForm({
                    name: "",
                    state:
                      states[0]?.id !== null ? String(states[0]?.id ?? "") : "",
                    is_active: true,
                  });
                  setEditing(null);
                  setDialogOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create new
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editing ? "Edit District" : "Create New District"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>State *</Label>
                  <Select
                    value={String(form.state ?? "")}
                    onValueChange={v =>
                      setForm(prev => ({ ...prev, state: v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map(s => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="district-name">Name *</Label>
                  <Input
                    id="district-name"
                    value={form.name}
                    onChange={e =>
                      setForm(prev => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="District name"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="district-active"
                    checked={form.is_active ?? true}
                    onCheckedChange={c =>
                      setForm(prev => ({ ...prev, is_active: c }))
                    }
                  />
                  <Label htmlFor="district-active">Active</Label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    disabled={submitLoading}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={submitLoading}>
                    {submitLoading ? (
                      <CustomLoader size="xs" />
                    ) : editing ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        }
      >
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <CustomLoader size="md" />
          </div>
        ) : (
          <DataTable
            embedded
            hideToolbar
            tableId="list-master-district"
            columns={tableColumns}
            data={districts}
            getRowKey={d => d.id}
            emptyMessage={
              totalItems === 0 && !searchTerm.trim()
                ? "No districts yet. Create one to get started."
                : "No districts found matching your search."
            }
          />
        )}

        {totalItems > 0 && (
          <div className="mt-4 border-t pt-4">
            <CommonPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalCount={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={size => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </ListMasterPageLayout>

      <Dialog
        open={!!deleting}
        onOpenChange={open => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete District</DialogTitle>
          </DialogHeader>
          {deleting && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <strong>{deleting.name}</strong>
                ?
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleting(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? <CustomLoader size="xs" /> : "Delete"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
